import moment, { Moment } from 'moment';
import { WidgetProps } from './components/Widget';

export interface HasId {
    id: string;
}

export const LessonFormats = [
    'private',
    'semi-private',
    'group'
];

export const SessionTypes = [
    {
        name: 'lessons',
        formats: LessonFormats,
    },
    {
        name: 'supervision',
        formats: [],
    },
    {
        name: 'guiding',
        formats: [],
    },
    {
        name: 'rental',
        formats: [],
    }
];

export type BookingSkill =
    | 'first time'
    | 'beginner'
    | 'intermediate'
    | 'advanced'
    | 'expert';

export const BookingSkills: BookingSkill[] = [
    'first time',
    'beginner',
    'intermediate',
    'advanced',
    'expert'
];

export type Skill = string;

export interface MemberActivityDetails {
    skill: string | null;
    type: string | null;
}

export interface GroupMember {
    readonly index: number;
    readonly name: string;
    readonly email: string;
    readonly tel: string;
    readonly editable: boolean;
    readonly nameValid: boolean;
    readonly emailValid: boolean;
    readonly required: boolean;
    readonly requestTel: boolean;
}

export interface IQuestion {
    /** qn: question number, index of question in Question[] */
    readonly qn: number;
    readonly type: string;
    readonly activity?: string;
    readonly index?: number;
    readonly show: boolean;
    readonly skip: boolean;
    readonly editing: boolean;
}

export interface WhenQuestion extends IQuestion {
    readonly type: 'when';
    readonly start: Moment | null;
    readonly end: Moment | null;
    readonly nDays: number;
    readonly nextDisabled: boolean;
    readonly location: string;
    readonly activities: string[];
}

export interface ActivitiesQuestion extends IQuestion {
    readonly type: 'activities';
    readonly days: { [activity: string]: number };
    readonly nextDisabled: boolean;
    readonly maxDays: number;
}

export interface GroupSizeQuestion extends IQuestion {
    readonly type: 'group-size';
    readonly groupSize: number;
    readonly maxGroupSize: number;
}

export interface WhoQuestion extends IQuestion {
    readonly type: 'who';
    readonly group: GroupMember[];
    readonly groupSize: number;
    readonly nextDisabled: boolean;
}

export interface WhoIsDoingQuestion extends IQuestion {
    readonly type: 'who-is-doing';
    readonly activity: string;
    readonly groupNames: string[];
    readonly selected: boolean[];
    readonly nextDisabled: boolean;
    readonly groupSize: number;
}

export interface SkillsQuestion extends IQuestion {
    readonly type: 'skill';
    readonly groupSize: number;
    readonly activity: string;
    readonly index: number;
    readonly name: string;
    readonly skill: string;
}

export interface ActivityTypeQuestion extends IQuestion {
    readonly type: 'activity-type';
    readonly activity: string;
    readonly activityType: string;
}

export interface ConfirmQuestion extends IQuestion {
    readonly type: 'confirm';
    readonly request: WidgetBookingRequestV1 | null;
    readonly apiDomain: string;
    readonly fakeApi: boolean;
}

export interface SentQuestion extends IQuestion {
    readonly type: 'sent';
    readonly response: WidgetBookingResponseV1 | null;
}

export type Question =
    | WhenQuestion
    | ActivitiesQuestion
    | GroupSizeQuestion
    | WhoQuestion
    | WhoIsDoingQuestion
    | SkillsQuestion
    | ActivityTypeQuestion
    | ConfirmQuestion
    | SentQuestion
    ;

export interface BotState {
    readonly parameters: WidgetProps;
    readonly expanded: boolean;
    readonly sent: boolean;
    readonly questions: Question[];
    readonly language: string | null;
    readonly request: WidgetBookingRequestV1 | null;
}

export interface WidgetBookingRequestV1 {
    schoolId: string;
    start: moment.Moment;
    end: moment.Moment;
    name: string;
    email: string;
    tel: string;
    language: string;
    group: {
        name: string;
        email: string;
        tel: string;
        skills: {
            [name: string]: BookingSkill;
        };
    }[];
    activities: {
        activity: string;
        days: number;
        sessionType: string;
    }[];
}

export type WidgetBookingResponseV1 =
    | { code: 'SUCCESS', bookingId: string, viewUrl: string }
    | { code: 'SCHOOL_NOT_FOUND' }
    | { code: 'MISSING_NAME' }
    | { code: 'MISSING_EMAIL' }
    | { code: 'ERROR', message: string }
    ;
